import Markdown from 'react-markdown';
import PropTypes from 'prop-types';
import React from 'react';
import Grid from '@mui/material/Grid';
import { CaretIcon, Link, Typography } from '@tkww/the-bash-frontend';

import Divider from 'components/Divider';
import PhotoCopy from 'components/PhotoCopy';
import Image from 'components/Image';
import getConceptSchemeTitle from 'helpers/getConceptSchemeTitle';
import getIngredientAltText from 'helpers/getIngredientAltText';
import { paragraph, link } from 'helpers/MarkdownRenderers';
import { BEVERAGE, FOOD, VENUES } from 'constants/ConceptSchemes';
import { useDispatch } from 'react-redux';
import { themeIngredientMarketplaceClick } from 'state/modules/analytics';
import getResizeImageUrl from 'helpers/media/getResizeImageUrl';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

const getServiceLinkData = ({ concept, pluralTitle, serviceAttribute }) => {
  if (concept && concept.conceptScheme !== VENUES.conceptScheme && concept.gmSlug) {
    const { gmSlug: slug, conceptScheme, title } = concept;
    const text =
      conceptScheme === BEVERAGE.conceptScheme || conceptScheme === FOOD.conceptScheme
        ? getConceptSchemeTitle(conceptScheme)
        : pluralTitle;
    return { slug, text, title };
  } else if (serviceAttribute && serviceAttribute.conceptScheme === FOOD.conceptScheme) {
    return {
      slug: 'caterer',
      text: 'Caterers',
      title: 'Caterer',
    };
  }

  return null;
};

const StyledHeading = styled(Typography)(({ theme }) => ({
  textTransform: 'uppercase',
  margin: theme.spacing(0, 0, 1),
}));

export const ThemeIngredient = (props) => {
  const { themeName, ingredient, index, showPinterestButton } = props;
  const altText = ingredient.image.altText
    ? ingredient.image.altText
    : getIngredientAltText(ingredient.type, ingredient.title);

  const serviceLinkData = getServiceLinkData(ingredient);

  const dispatch = useDispatch();
  const onClick = () =>
    dispatch(themeIngredientMarketplaceClick(themeName, ingredient, serviceLinkData.title));

  const mobileImageUrl = getResizeImageUrl(ingredient.image.url, 379, 'h', true);
  const desktopImageUrl = getResizeImageUrl(ingredient.image.url, 576, 'h', true);

  return (
    <div role="region" aria-label={ingredient.slug}>
      <Divider />
      <Grid
        container
        id={ingredient.slug}
        sx={{
          justifyContent: 'space-between',
          '& > div': {
            maxWidth: { md: '49%' },
          },
        }}
        direction={index % 2 === 0 ? 'row' : 'row-reverse'}
        sm={12}
        spacing={0}
      >
        <Grid item md={6} sm={12}>
          <Box mb={{ xs: 2, md: 4 }}>
            <StyledHeading variant="h5">{ingredient.type}</StyledHeading>
            <StyledHeading variant="h2">{ingredient.title}</StyledHeading>
            <Markdown
              components={{
                a: link,
                p: paragraph,
              }}
            >
              {ingredient.description}
            </Markdown>
          </Box>
          {serviceLinkData && (
            <Link
              type="standalone"
              path={`/services/${serviceLinkData.slug}`}
              testid="service-link"
              onClick={onClick}
            >
              Explore Local {serviceLinkData.text}
              <CaretIcon direction="right" />
            </Link>
          )}
        </Grid>

        <Grid
          item
          sx={{
            '& .photo': {
              marginBottom: { xs: 2, md: 0 },
              '& img': {
                width: '100%',
              },
            },
          }}
          md={6}
          sm={12}
        >
          <PhotoCopy
            className="photo"
            title={ingredient.image.credit}
            titleUrl={ingredient.image.creditUrl}
            align={{ xs: 'left' }}
          >
            <Image
              alt={altText}
              pinterestDescription={ingredient.image.pinterestDescription}
              showPinterestButton={showPinterestButton}
              src={desktopImageUrl}
              srcMobile={mobileImageUrl}
            />
          </PhotoCopy>
        </Grid>
      </Grid>
    </div>
  );
};

ThemeIngredient.propTypes = {
  index: PropTypes.number,
  ingredient: PropTypes.shape({
    type: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    image: PropTypes.shape({}),
    slug: PropTypes.string.isRequired,
  }),
  themeName: PropTypes.string.isRequired,
  showPinterestButton: PropTypes.bool,
};

ThemeIngredient.defaultProps = {
  ingredient: {},
  index: 0,
  showPinterestButton: false,
};

export default ThemeIngredient;
